import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { maturationService } from '../../../services/maturationService';

const MeasurementScheduleScreen = ({ guardianProfile, onBack }) => {
  const [frequency, setFrequency] = useState(guardianProfile?.measurementFrequency || 'monthly');
  const [reminderEnabled, setReminderEnabled] = useState(guardianProfile?.reminderEnabled ?? true);
  const [nextDate, setNextDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    calculateNextDate(frequency);
  }, [frequency]);

  const calculateNextDate = (freq) => {
    const now = new Date();
    let next = new Date(now);

    switch (freq) {
      case 'weekly':
        next?.setDate(now?.getDate() + 7);
        break;
      case 'biweekly':
        next?.setDate(now?.getDate() + 14);
        break;
      case 'monthly':
        next?.setMonth(now?.getMonth() + 1);
        break;
      default:
        next?.setMonth(now?.getMonth() + 1);
    }

    setNextDate(next);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await maturationService?.updateGuardianProfile({
        measurementFrequency: frequency,
        nextMeasurementDate: nextDate?.toISOString(),
        reminderEnabled: reminderEnabled
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving schedule:', err);
      setError(err?.message || 'Failed to save measurement schedule');
    } finally {
      setSaving(false);
    }
  };

  const frequencyOptions = [
    { value: 'weekly', label: 'Weekly', description: 'Every 7 days' },
    { value: 'biweekly', label: 'Bi-weekly', description: 'Every 14 days' },
    { value: 'monthly', label: 'Monthly', description: 'Every month' }
  ];

  const getFrequencyDescription = () => {
    switch (frequency) {
      case 'weekly':
        return 'Measurements every 7 days provide the most detailed growth tracking but require consistent effort.';
      case 'biweekly':
        return 'Measurements every 14 days balance detail with practicality for most families.';
      case 'monthly':
        return 'Monthly measurements are the minimum recommended frequency for reliable growth velocity analysis.';
      default:
        return '';
    }
  };

  const getDaysUntilNext = () => {
    if (!nextDate) return 0;
    const now = new Date();
    const diff = nextDate - now;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center">
        <Button 
          onClick={onBack}
          variant="ghost"
          size="sm"
          iconName="ArrowLeft"
        >
          Back
        </Button>
        <h1 className="text-3xl font-bold text-text-primary ml-4">Measurement Schedule</h1>
      </div>

      {error && (
        <div className="bg-error/10 border border-error rounded-lg p-4">
          <div className="flex items-start">
            <Icon name="AlertCircle" className="w-5 h-5 text-error mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-text-primary mb-1">Error</p>
              <p className="text-text-secondary text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-success/10 border border-success rounded-lg p-4">
          <div className="flex items-start">
            <Icon name="CheckCircle" className="w-5 h-5 text-success mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-text-primary mb-1">Schedule Updated</p>
              <p className="text-text-secondary text-sm">Your measurement preferences have been saved successfully.</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start mb-4">
          <div className="bg-primary/10 p-3 rounded-full mr-4">
            <Icon name="Calendar" className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-text-primary mb-2">Measurement Frequency</h2>
            <p className="text-text-secondary text-sm mb-4">
              Set how often you want to record growth measurements. Consistent measurements are essential for accurate growth velocity analysis.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <Select
            label="Frequency"
            value={frequency}
            onChange={setFrequency}
            options={frequencyOptions}
            className="max-w-md"
          />

          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start">
              <Icon name="Info" className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
              <p className="text-text-secondary text-sm">{getFrequencyDescription()}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start mb-4">
          <div className="bg-success/10 p-3 rounded-full mr-4">
            <Icon name="Clock" className="w-6 h-6 text-success" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-text-primary mb-2">Next Scheduled Measurement</h2>
            <p className="text-text-secondary text-sm mb-4">
              Based on your selected frequency, your next measurement is scheduled for:
            </p>
          </div>
        </div>

        {nextDate && (
          <div className="bg-surface rounded-lg p-6 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-primary mb-1">
                  {nextDate?.toLocaleDateString('en-GB', { 
                    day: '2-digit', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </p>
                <p className="text-text-secondary text-sm">
                  {getDaysUntilNext()} {getDaysUntilNext() === 1 ? 'day' : 'days'} from now
                </p>
              </div>
              <div className="bg-primary/10 p-4 rounded-full">
                <Icon name="CalendarCheck" className="w-8 h-8 text-primary" />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start mb-4">
          <div className="bg-warning/10 p-3 rounded-full mr-4">
            <Icon name="Bell" className="w-6 h-6 text-warning" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-text-primary mb-2">Email Reminders</h2>
            <p className="text-text-secondary text-sm mb-4">
              Receive email reminders when it is time to record measurements. Reminders are sent to your login email address.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between bg-surface rounded-lg p-4 border border-border">
          <div className="flex items-center">
            <Icon name="Mail" className="w-5 h-5 text-text-secondary mr-3" />
            <div>
              <p className="text-text-primary font-medium">Enable Email Reminders</p>
              <p className="text-text-secondary text-sm">Get notified before each scheduled measurement</p>
            </div>
          </div>
          <button
            onClick={() => setReminderEnabled(!reminderEnabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              reminderEnabled ? 'bg-primary' : 'bg-border'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                reminderEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {reminderEnabled && guardianProfile?.lastReminderSentAt && (
          <div className="mt-4 bg-primary/5 border border-primary/20 rounded-lg p-3">
            <div className="flex items-start">
              <Icon name="CheckCircle" className="w-4 h-4 text-primary mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-text-secondary text-sm">
                Last reminder sent: {new Date(guardianProfile?.lastReminderSentAt)?.toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex items-start">
          <Icon name="Lightbulb" className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-text-primary font-medium mb-2">Tips for Consistent Data Collection</p>
            <ul className="text-text-secondary text-sm space-y-1 list-disc list-inside">
              <li>Measure at the same time of day (preferably morning)</li>
              <li>Take two back-to-back measurements for quality control</li>
              <li>Use the same measurement location and technique each time</li>
              <li>Record any notes about measurement conditions or concerns</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <Button
          onClick={onBack}
          variant="outline"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          loading={saving}
          iconName="Save"
        >
          Save Schedule
        </Button>
      </div>
    </div>
  );
};

export default MeasurementScheduleScreen;